<template>
  <div class="mining-table">
    <el-skeleton :loading="activityListStatusInjected === 'pending'" animated>
      <template #template>
        <el-skeleton-item />
      </template>

      <template #default>
        <div class="card main-cards statistics">
          <div class="stats-header">
            <img :src="tokens[props.token].toString()" :alt="title" />
            <h3>{{ $t(`${tokensConfig[token].label}`, { count: 0 }) }}</h3>
          </div>
          <p v-if="showDescription" class="stats-intro-p">
            {{ getCurrentTokenDisclaimer() }}
          </p>

          <div class="table">
            <el-table
              :data="getListItems()"
              :empty-text="$t('coreNoData')"
              style="width: 100%"
              :border="false"
              :table-layout="$device.isMobile ? 'auto' : 'fixed'"
              :header-cell-style="{ background: 'linear-gradient(180deg, #071821 0%, #040E13 100%) !important' }"
              class="activity-list"
            >
              <el-table-column prop="amount" :label="$t('dashboardPurchasesAmount')">
                <template #default="scope">
                  <div class="with-icon">
                    <img :src="tokens[props.token].toString()" loading="lazy" alt="" width="32" />
                    {{ $formatValue(scope.row.amount) }}
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="createdAt" :label="$t('dashboardPurchasesCreated')">
                <template #default="scope">
                  {{ formatDate(scope.row.createdAt) }}
                </template>
              </el-table-column>
              <el-table-column prop="available" :label="$t('dashboardMiningAvailable')">
                <template #default="scope">
                  {{ Number(scope.row.available) ? $formatValue(scope.row.available) : scope.row.available }}
                </template>
              </el-table-column>
            </el-table>

            <div v-if="!getListItems() || !getListItems().length" class="controls">
              <button class="w-button btn-primary small" @click="$emit('openPurchaseToken')">
                {{ $t('dashboardBuyPearls') }}
              </button>
            </div>
            <div v-if="miningsTableData && miningsTableData.length > SHORT_LIST_ITEMS_LENGTH" class="controls">
              <SwitcherButton
                :active-text="$t('appStatisticShowMore')"
                :inactive-text="$t('appStatisticShowLess')"
                :change="() => (isShowShortList = !isShowShortList)"
              />
            </div>
          </div>
        </div>
      </template>
    </el-skeleton>
  </div>
</template>

<script setup lang="ts">
import { ElTable, ElTableColumn } from 'element-plus';
import { tokensConfig, Tokens, type MiningsTableRow, SHORT_LIST_ITEMS_LENGTH } from '~/utils/constants';
import tokens from 'public/img/tokens';

const store = useMainStore();

const { t } = useI18n();
const activityListStatusInjected = inject<Ref<string>>('activityListStatus');

const props = defineProps<{
  title: string;
  token: Tokens;
  miningsTableData: MiningsTableRow[];
  showDescription?: boolean;
}>();
defineEmits(['openPurchaseToken']);

const isShowShortList = ref<boolean>(true);

function getListItems(): MiningsTableRow[] {
  if (!props.miningsTableData) {
    return [];
  }

  if (isShowShortList.value) {
    return props.miningsTableData.slice(0, SHORT_LIST_ITEMS_LENGTH);
  }

  return props.miningsTableData;
}

function getCurrentTokenDisclaimer(): string {
  return store.currentToken === Tokens.coal
    ? t('dashboardPurchasesCoalDisclaimer')
    : t('dashboardPurchasesGoldOreDisclaimer');
}

function formatDate(value: string | number): string {
  if (!value) {
    return '';
  }
  return new Date(value).toLocaleString('en-GB');
}
</script>

<style scoped lang="scss">
.controls {
  display: flex;
  justify-content: center;
  padding-top: 40px;
  text-align: center;

  button {
    min-width: 250px;

    .token-name {
      text-transform: lowercase;
    }
  }

  @media screen and (max-width: 767px) {
    padding-top: 24px;
    padding-bottom: 24px;

    button {
      max-width: fit-content;
    }
  }
}

.showMoreButton {
  width: 272px;
}

.purchase-rec {
  display: flex;
}

.text-block {
  margin-bottom: 10px;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.667em;
}

.table {
  @media screen and (max-width: 767px) {
    margin-left: -16px;
    margin-right: -16px;
  }

  @media screen and (max-width: 479px) {
    margin-left: -38px;
    margin-right: -38px;
    position: static;
    overflow: auto;
    background: var(--blue-3);
    border-top: 1px solid var(--blue-2);
    border-bottom: 1px solid var(--blue-2);
  }
}

.card.main-cards.statistics {
  padding: 0px 16px;
  padding-bottom: 24px;
}

@media screen and (max-width: 479px) {
  .card.main-cards.statistics {
    margin-top: -16px;
    padding-bottom: 0;
  }
}

.center {
  text-align: center;
}

.with-icon {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;

  img {
    margin-left: -8px;
  }
}

.stats {
  &-header {
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      height: 50px;
      width: 50px;
    }

    h3 {
      color: #fff;
      font-weight: 700;
      margin-left: 10px;
      font-family: Grenze;
      margin-top: 0;
      margin-bottom: 0;
      text-transform: capitalize;
    }
  }
}
</style>

<style>
.mining-table {
  .el-skeleton {
    height: 370px;
    margin-bottom: 24px;

    .el-skeleton__item {
      height: 370px;
      border-radius: 12px;
    }
  }
}
</style>
